import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface MollieAccount {
  id: string;
  pta_id: string;
  mollie_organization_id: string | null;
  mollie_profile_id: string | null;
  onboarding_status: string;
  can_receive_payments: boolean;
  connected_at: string;
}

interface UseMollieIntegrationReturn {
  mollieAccount: MollieAccount | null;
  loading: boolean;
  isConnected: boolean;
  canReceivePayments: boolean;
  refetch: () => Promise<void>;
}

export function useMollieIntegration(ptaId: string | null): UseMollieIntegrationReturn {
  const [mollieAccount, setMollieAccount] = useState<MollieAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMollieAccount = async () => {
    if (!ptaId) {
      setMollieAccount(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('get_pta_mollie_account_safe', { p_pta_id: ptaId })
        .maybeSingle();

      if (error) {
        console.error('Error fetching Mollie account:', error);
        setMollieAccount(null);
      } else {
        setMollieAccount(data);
      }
    } catch (err) {
      console.error('Error:', err);
      setMollieAccount(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMollieAccount();
  }, [ptaId]);

  return {
    mollieAccount,
    loading,
    isConnected: !!mollieAccount?.mollie_organization_id,
    canReceivePayments: mollieAccount?.can_receive_payments ?? false,
    refetch: fetchMollieAccount,
  };
}
